import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, Tag, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useCategoryTree } from "@/hooks/useCategoryTree";

interface SearchSuggestion {
  type: "category" | "product" | "search";
  label: string;
  sublabel?: string;
  href: string;
  icon?: React.ReactNode;
}

export function CategoryAwareSearch() {
  const navigate = useNavigate();
  const { tree } = useCategoryTree();
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize parent categories to prevent re-renders
  const parentCategories = useMemo(() => tree, [tree]);

  // Generate suggestions based on query - use callback instead of effect
  const computedSuggestions = useMemo(() => {
    if (!query.trim()) return [];

    const lowerQuery = query.toLowerCase();
    const results: SearchSuggestion[] = [];

    // Flatten categories inline for search
    const flattenCategories = (cats: typeof tree, path: string[] = []): Array<{ name: string; slug: string; path: string[]; fullPath: string }> => {
      const result: Array<{ name: string; slug: string; path: string[]; fullPath: string }> = [];
      cats.forEach((cat) => {
        const currentPath = [...path, cat.slug];
        result.push({
          name: cat.name,
          slug: cat.slug,
          path: currentPath,
          fullPath: [...path.map((_, i) => cats[i]?.name || ""), cat.name].filter(Boolean).join(" > "),
        });
        if (cat.children?.length) {
          result.push(...flattenCategories(cat.children, currentPath));
        }
      });
      return result;
    };

    const flatCats = flattenCategories(parentCategories);

    // Find matching categories
    const matchingCategories = flatCats
      .filter(
        (cat) =>
          cat.name.toLowerCase().includes(lowerQuery) ||
          cat.fullPath.toLowerCase().includes(lowerQuery)
      )
      .slice(0, 4);

    matchingCategories.forEach((cat) => {
      results.push({
        type: "category",
        label: `Search for "${query}" in ${cat.name}`,
        sublabel: cat.fullPath,
        href: `/${cat.path.join("/")}?search=${encodeURIComponent(query)}`,
        icon: <Tag className="h-4 w-4 text-plum" />,
      });

      results.push({
        type: "category",
        label: `View ${cat.name} Category`,
        sublabel: cat.fullPath,
        href: `/${cat.path.join("/")}`,
        icon: <Package className="h-4 w-4 text-muted-foreground" />,
      });
    });

    // Add general search option
    results.push({
      type: "search",
      label: `Search for "${query}"`,
      sublabel: selectedCategory !== "all" 
        ? `in ${parentCategories.find((c) => c.slug === selectedCategory)?.name || "All"}` 
        : "in All Categories",
      href: selectedCategory !== "all"
        ? `/${selectedCategory}?search=${encodeURIComponent(query)}`
        : `/shop?search=${encodeURIComponent(query)}`,
      icon: <Search className="h-4 w-4 text-muted-foreground" />,
    });

    return results.slice(0, 8);
  }, [query, selectedCategory, parentCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    const searchPath =
      selectedCategory !== "all"
        ? `/${selectedCategory}?search=${encodeURIComponent(query.trim())}`
        : `/shop?search=${encodeURIComponent(query.trim())}`;

    navigate(searchPath);
    setQuery("");
    setIsOpen(false);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    navigate(suggestion.href);
    setQuery("");
    setIsOpen(false);
  };

  const showSuggestions = isOpen && computedSuggestions.length > 0;

  return (
    <div ref={containerRef} className="relative flex-1 max-w-2xl" role="search" aria-label="Product search">
      {/* Solid `bg-card`, not `bg-muted/30`: at 30% opacity the field took its
          colour from whatever sat behind it, which on the dark header read as a
          murky grey rather than a light, obvious target. */}
      <form onSubmit={handleSubmit} className="flex items-center w-full h-10 rounded-full border border-border bg-card overflow-hidden">
        {/* Category Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              aria-label={`Filter by category: ${selectedCategory === "all" ? "All Categories" : parentCategories.find((c) => c.slug === selectedCategory)?.name || "All"}`}
              className="h-10 rounded-none border-0 bg-transparent hover:bg-muted/50 px-3 gap-1 min-w-[80px] flex-shrink-0 shadow-none"
            >
              <span className="truncate text-sm">
                {selectedCategory === "all"
                  ? "All"
                  : parentCategories.find((c) => c.slug === selectedCategory)?.name || "All"}
              </span>
              <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={() => setSelectedCategory("all")}>
              All Categories
            </DropdownMenuItem>
            {parentCategories.map((cat) => (
              <DropdownMenuItem
                key={cat.id}
                onClick={() => setSelectedCategory(cat.slug)}
              >
                {cat.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Divider */}
        <div className="w-px h-5 bg-border flex-shrink-0" />

        {/* Search Input */}
        <div className="relative flex-1 min-w-0">
          <Input
            aria-label="Search products"
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            role="combobox"
            ref={inputRef}
            type="search"
            placeholder="Search modest fashion..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => query && setIsOpen(true)}
            className="!h-10 border-0 bg-transparent rounded-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 text-sm px-3"
          />
        </div>

        {/* Search Button */}
        <Button
          type="submit"
          className="h-10 rounded-l-none rounded-r-full bg-plum hover:bg-plum-dark px-5 flex-shrink-0"
          aria-label="Search"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
        </Button>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-popover border rounded-xl shadow-lg z-50" role="listbox" aria-label="Search suggestions">
          <ScrollArea className="max-h-[400px]">
            <div className="p-2 space-y-0.5">
              {computedSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-accent transition-colors flex items-start gap-3"
                  role="option"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className="mt-0.5">{suggestion.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {suggestion.label}
                    </div>
                    {suggestion.sublabel && (
                      <div className="text-xs text-muted-foreground truncate">
                        {suggestion.sublabel}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

export default CategoryAwareSearch;
